import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Search, BookOpen } from "lucide-react";

function WordBankPanel() {
  const [bank, setBank] = useState({ builtin: [], custom: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ word: "", meaning: "" });
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/word/bank");
      setBank(r.data);
    } catch { toast.error("Failed to load word bank"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const word = form.word.replace(/[^a-zA-Z]/g, "");
    if (word.length < 3) { toast.error("Word must be at least 3 letters"); return; }
    setBusy(true);
    try {
      await api.post("/admin/word/bank", { word, meaning: form.meaning.trim() });
      toast.success(`"${word.toUpperCase()}" added to the rotation`);
      setForm({ word: "", meaning: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const remove = async (wid, word) => {
    if (!window.confirm(`Remove "${word}" from the word bank?`)) return;
    try {
      await api.delete(`/admin/word/bank/${wid}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const q = search.trim().toLowerCase();
  const matches = (w) => !q || (w.word || "").toLowerCase().includes(q) || (w.meaning || "").toLowerCase().includes(q);
  const custom = (bank.custom || []).filter(matches);
  const builtin = (bank.builtin || []).filter(matches);

  return (
    <div className="space-y-6" data-testid="word-bank-panel">
      <div className="brutal-card p-5">
        <h3 className="font-black uppercase mb-1 flex items-center gap-2"><BookOpen size={18} /> Word Bank</h3>
        <p className="text-sm text-neutral-600 mb-4">
          The curated dictionary for the daily <strong>Word Building</strong> game. One word is auto-selected each day (rotating through the whole list, IST). Every word shows its <strong>meaning</strong> on reveal, so employees learn professional vocabulary. Add your own words below — they join the rotation immediately. Built-in words cannot be deleted.
        </p>
        <div className="grid md:grid-cols-[1fr_2fr_auto] gap-2 items-end">
          <div>
            <label className="text-[11px] font-black uppercase block">Word</label>
            <input data-testid="wb-word-input" value={form.word} onChange={(e)=>setForm({...form, word:e.target.value})} placeholder="e.g. RESILIENT" className="brutal-input uppercase" maxLength={20}/>
          </div>
          <div>
            <label className="text-[11px] font-black uppercase block">Meaning</label>
            <input data-testid="wb-meaning-input" value={form.meaning} onChange={(e)=>setForm({...form, meaning:e.target.value})} placeholder="Short definition shown on reveal" className="brutal-input"/>
          </div>
          <button data-testid="wb-add-btn" onClick={add} disabled={busy} className="brutal-btn flex items-center gap-2 h-[42px]"><Plus size={16}/> {busy ? "Adding…" : "Add"}</button>
        </div>
      </div>

      <div className="brutal-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-black uppercase flex items-center gap-2">Words in Rotation ({bank.total})</h3>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <input data-testid="wb-search" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search word / meaning…" className="brutal-input pl-7 text-sm py-1" style={{ width: 260 }}/>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 py-6 text-center">Loading word bank…</div>
        ) : (
          <div className="space-y-5">
            <div>
              <div className="text-xs uppercase font-mono text-neutral-500 mb-2">Custom words ({custom.length})</div>
              {custom.length === 0 ? (
                <p className="text-sm text-neutral-400">No custom words yet — add one above.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="wb-custom-list">
                  {custom.map((w) => (
                    <div key={w.id} data-testid={`wb-custom-${w.word}`} className="border-2 border-black p-3 flex items-start justify-between gap-2 bg-[#FFFCE5]">
                      <div className="min-w-0">
                        <div className="font-black uppercase">{w.word}</div>
                        <div className="text-xs text-neutral-600">{w.meaning || <span className="text-neutral-400 italic">No meaning set</span>}</div>
                      </div>
                      <button data-testid={`wb-del-${w.word}`} onClick={()=>remove(w.id, w.word)} title="Remove word" className="p-1 border-2 border-black hover:bg-[#FF3B30] hover:text-white flex-shrink-0"><Trash2 size={13}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs uppercase font-mono text-neutral-500 mb-2">Built-in words ({builtin.length})</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="wb-builtin-list">
                {builtin.map((w, i) => (
                  <div key={`${w.word}-${i}`} className="border-2 border-neutral-300 p-3 flex items-start justify-between gap-2 bg-white">
                    <div className="min-w-0">
                      <div className="font-black uppercase">{w.word}</div>
                      <div className="text-xs text-neutral-600">{w.meaning || <span className="text-neutral-400 italic">No meaning set</span>}</div>
                    </div>
                    <span className="text-[10px] uppercase font-black border border-neutral-300 px-1.5 py-0.5 text-neutral-400 flex-shrink-0">Built-in</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WordBankPanel;
